// /Users/sam/repos/itavan/ecoseternos/ecoseternos-workspace/projects/itavan/lib/core/services/src/memorial-details.service.ts
import { inject, Injectable } from '@angular/core';
import {
  FNGetMemorialById,
  MemorialMediaRow,
  MemorialMediaRowWithSignedUrl,
  MemorialRow,
  MemorialRowWithMedia,
  SupabaseDatabase,
  SupabaseStorage,
} from '@itavan/lib/core/supabase';
import { AudioCompressorService } from './audio-compressor.service';
import { LoggerService } from './logger.service';

@Injectable({ providedIn: 'root' })
export class MemorialDetailsService {
  private db = inject(SupabaseDatabase);
  private storage = inject(SupabaseStorage);
  private logger = inject(LoggerService);
  private audioCompressor = inject(AudioCompressorService);

  private readonly THUMBNAIL_MAX_SIZE = 640;
  private readonly IMAGE_MAX_SIZE = 1920;
  private readonly IMAGE_EXTENSION = 'webp';
  private readonly COMPRESS_QUALITY_LOW = 80;
  private readonly COMPRESS_QUALITY_HIGH = 80;
  private readonly BUCKET = 'ecoseternos-storage';

  /**
   * Loads a memorial by ID (for the owner/edit use case).
   * Does a direct query to the memorial table.
   */
  async getMemorialById(memorialId: string): Promise<MemorialRow> {
    const { data, error } = await this.db
      .from('memorial')
      .select('*')
      .eq('id', memorialId)
      .eq('is_active', true)
      .single<MemorialRow>();

    if (error) throw error;
    if (!data) throw new Error('Memorial no encontrado');

    return data;
  }

  async getByQr(qrCodeId: string): Promise<{
    memorial: MemorialRowWithMedia;
    media: MemorialMediaRowWithSignedUrl[];
    qrCodeUrl: string;
  }> {
    const memorialData = await this.db.rpc<FNGetMemorialById>('get_memorial_by_id', {
      p_id: qrCodeId,
    });

    if (!memorialData || memorialData.length === 0) {
      throw new Error('Este memorial no está disponible.');
    }

    const memorial = memorialData[0] as unknown as MemorialRowWithMedia;
    const mediaRows = (memorial.media_files ?? []) as unknown as MemorialMediaRow[];
    const media = await this.buildMediaWithUrls(memorial.id, mediaRows, memorial.is_public);
    const qrCodeUrl = await this.loadQRCode(memorial.id);

    return { memorial, media, qrCodeUrl };
  }

  async loadQRCode(memorialId: string): Promise<string> {
    const {
      data: { signedUrl },
    } = await this.storage.createSignedUrl(
      this.BUCKET,
      `memorial/${memorialId}/qrcode_${memorialId}.png`
    );
    return signedUrl;
  }

  /**
   * Loads gallery media for a memorial (excludes AVATAR).
   * Queries DB + generates signed/public URLs.
   */
  async loadGalleryMedia(
    memorialId: string,
    isPublic: boolean
  ): Promise<MemorialMediaRowWithSignedUrl[]> {
    const { data: mediaData, error } = await this.db
      .from('memorial_media')
      .select('*')
      .eq('memorial_id', memorialId)
      .eq('media_role', 'GALLERY')
      .order('created_at', { ascending: false });

    if (error) throw error;
    if (!mediaData || mediaData.length === 0) return [];

    return this.buildMediaWithUrls(memorialId, mediaData as MemorialMediaRow[], isPublic);
  }

  async buildMediaWithUrls(
    memorialId: string,
    mediaData: MemorialMediaRow[],
    isPublic: boolean
  ): Promise<MemorialMediaRowWithSignedUrl[]> {
    if (!mediaData || mediaData.length === 0) return [];

    const items = await Promise.all(
      mediaData.map(async (media) => {
        const filePath = `memorial/${memorialId}/${media.file_name}`;
        const isVideo = media.mime_type.startsWith('video/');
        const isImage = media.mime_type.startsWith('image/');

        let url: string | null = null;
        let thumbnailUrl: string | null = null;

        if (isPublic) {
          url = this.storage.getPublicUrl(this.BUCKET, filePath);
          thumbnailUrl = isVideo
            ? this.storage.getPublicUrl(
                this.BUCKET,
                filePath.replace('.mp4', `.${this.IMAGE_EXTENSION}`)
              )
            : this.storage.getPublicUrl(
                this.BUCKET,
                filePath.replace(
                  `.${this.IMAGE_EXTENSION}`,
                  `_${this.THUMBNAIL_MAX_SIZE}.${this.IMAGE_EXTENSION}`
                )
              );
        } else {
          const { data: urlData } = await this.storage.createSignedUrl(this.BUCKET, filePath, 3600);
          url = urlData.signedUrl;

          if (isImage) {
            const { data: thumbs } = await this.storage.createSignedUrl(
              this.BUCKET,
              filePath.replace(
                `.${this.IMAGE_EXTENSION}`,
                `_${this.THUMBNAIL_MAX_SIZE}.${this.IMAGE_EXTENSION}`
              ),
              3600
            );
            thumbnailUrl = thumbs.signedUrl;
          }
          if (isVideo) {
            const { data: poster } = await this.storage.createSignedUrl(
              this.BUCKET,
              filePath.replace('.mp4', `.${this.IMAGE_EXTENSION}`),
              3600
            );
            thumbnailUrl = poster.signedUrl;
          }
        }

        return {
          ...media,
          signed_url: url,
          thumbnail_url: thumbnailUrl,
        } as MemorialMediaRowWithSignedUrl;
      })
    );

    return items;
  }

  getAvatarUrl(media: MemorialMediaRowWithSignedUrl[]): string | null {
    const a = media.find((m) => m.media_role === 'AVATAR');
    return a?.thumbnail_url || null;
  }

  /**
   * Loads the avatar for a memorial from DB + generates signed URL.
   * Returns null if no avatar exists.
   */
  async loadAvatar(
    memorialId: string
  ): Promise<{ id: string; file_name: string; signed_url?: string } | null> {
    const { data, error } = await this.db
      .from('memorial_media')
      .select('*')
      .eq('memorial_id', memorialId)
      .eq('media_role', 'AVATAR')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw error;

    const current = data && data.length > 0 ? data[0] : null;
    if (!current) return null;

    const { data: urlData } = await this.storage.createSignedUrl(
      this.BUCKET,
      `memorial/${memorialId}/${current.file_name.split('.')[0]}_${this.THUMBNAIL_MAX_SIZE}.${
        this.IMAGE_EXTENSION
      }`,
      3600
    );

    return {
      id: current.id,
      file_name: current.file_name,
      signed_url: urlData?.signedUrl,
    };
  }

  async updateMemorial(id: string, data: any): Promise<void> {
    const { error } = await this.db.from('memorial').update(data).eq('id', id);
    if (error) throw error;
  }

  /**
   * Unified upload: processes any file and uploads it with its thumbnail.
   */
  async uploadMedia(
    memorialId: string,
    file: File,
    mediaRole: 'AVATAR' | 'GALLERY',
    onProgress?: (progress: number) => void
  ): Promise<{ id: string; file_name: string; signed_url?: string }> {
    // 1. Process file (compress images, generate thumbnails, extract video metadata)
    const processed = await this.processFileForUpload(file, onProgress);

    // 2. Generate unique filename
    const baseFileName = crypto.randomUUID();
    const ext = processed.file.name.split('.').pop();
    const fileName = `${baseFileName}.${ext}`;
    const filePath = `memorial/${memorialId}/${fileName}`;

    // 3. Upload main file to Storage
    const { error: uploadError } = await this.storage.upload(this.BUCKET, filePath, processed.file);
    if (uploadError) throw uploadError;

    // 4. Upload thumbnail if present
    if (processed.thumbnail) {
      const thumbnailFileName =
        processed.mediaType === 'image'
          ? `${baseFileName}_${this.THUMBNAIL_MAX_SIZE}.${this.IMAGE_EXTENSION}`
          : `${baseFileName}.${this.IMAGE_EXTENSION}`;
      const { error: uploadThumbnailError } = await this.storage.upload(
        this.BUCKET,
        `memorial/${memorialId}/${thumbnailFileName}`,
        processed.thumbnail
      );
      if (uploadThumbnailError) throw uploadThumbnailError;
    }

    // 5. Single-instance roles: remove previous media
    if (mediaRole === 'AVATAR') {
      await this.removeCurrentAvatar(memorialId);
    }

    // 6. Insert metadata into Database
    const { data: dbData, error: dbError } = await this.db
      .from('memorial_media')
      .insert({
        memorial_id: memorialId,
        file_name: fileName,
        file_size: processed.file.size,
        width: processed.fileWidth,
        height: processed.fileHeight,
        duration: processed.duration ?? null,
        metadata: {
          thumbnail:
            processed.thumbnailWidth || processed.thumbnailHeight
              ? {
                  width: processed.thumbnailWidth,
                  height: processed.thumbnailHeight,
                  size: processed.thumbnail?.size,
                }
              : null,
          ...(processed.exactDuration ? { exactDuration: processed.exactDuration } : {}),
          ...(processed.originalSize ? { originalSize: processed.originalSize } : {}),
          ...(processed.originalFormat ? { originalFormat: processed.originalFormat } : {}),
          ...(processed.bitrate ? { bitrate: processed.bitrate } : {}),
          ...(processed.channels ? { channels: processed.channels } : {}),
        },
        mime_type: processed.file.type,
        media_role: mediaRole,
        title: file.name.split('.')[0],
      })
      .select()
      .single();

    if (dbError) {
      // Rollback: remove uploaded files from storage
      const filesToRemove = [filePath];
      if (processed.thumbnail) {
        const thumbnailFileName =
          processed.mediaType === 'image'
            ? `${baseFileName}_${this.THUMBNAIL_MAX_SIZE}.${this.IMAGE_EXTENSION}`
            : `${baseFileName}.${this.IMAGE_EXTENSION}`;
        filesToRemove.push(`memorial/${memorialId}/${thumbnailFileName}`);
      }
      await this.storage.remove(this.BUCKET, filesToRemove);
      throw dbError;
    }

    // 7. Get Signed URL for the thumbnail
    const thumbnailPath = processed.thumbnail
      ? `memorial/${memorialId}/${
          processed.mediaType === 'image'
            ? `${baseFileName}_${this.THUMBNAIL_MAX_SIZE}.${this.IMAGE_EXTENSION}`
            : `${baseFileName}.${this.IMAGE_EXTENSION}`
        }`
      : filePath;

    const { data: signed } = await this.storage.createSignedUrl(this.BUCKET, thumbnailPath, 3600);

    return {
      id: dbData.id,
      file_name: dbData.file_name,
      signed_url: signed?.signedUrl,
    };
  }

  async removeCurrentAvatar(memorialId: string) {
    this.logger.log('Removing current avatar for memorial:', memorialId);
    try {
      const { data, error } = await this.db
        .from('memorial_media')
        .select('*')
        .eq('memorial_id', memorialId)
        .eq('media_role', 'AVATAR')
        .limit(1);

      if (data && data.length > 0) {
        this.logger.log('Current avatar exists for memorial:', memorialId);
        const prev = data[0];
        const filePath = `memorial/${memorialId}/${prev.file_name}`;
        const thumbnailFilePath = `memorial/${memorialId}/${prev.file_name.replace(
          `.${this.IMAGE_EXTENSION}`,
          `_${this.THUMBNAIL_MAX_SIZE}.${this.IMAGE_EXTENSION}`
        )}`;

        await this.storage.remove(this.BUCKET, [filePath, thumbnailFilePath]);
        await this.db.from('memorial_media').delete().eq('id', prev.id);
      }
    } catch (e) {
      this.logger.error('Error removing previous avatar:', e);
      // Don't throw, just log
    }
  }

  /**
   * Public API: compresses a File to webp at the given max size.
   * Handles image loading internally.
   */
  async compressImage(
    file: File,
    maxSize: number = this.IMAGE_MAX_SIZE
  ): Promise<{ file: File; width: number; height: number }> {
    const img = await this.loadFileToHTMLImage(file);
    return this.compressToWebp(img, file, maxSize);
  }

  /**
   * Compresses an image and generates a thumbnail.
   * Used by uploadMedia.
   */
  private async compressImageWithThumbnail(file: File): Promise<{
    file: File;
    fileWidth: number;
    fileHeight: number;
    thumbnail: File;
    thumbnailWidth: number;
    thumbnailHeight: number;
  }> {
    const img = await this.loadFileToHTMLImage(file);
    const [compressedResult, compressedThumbnail] = await Promise.all([
      this.compressToWebp(img, file, this.IMAGE_MAX_SIZE),
      this.compressToWebp(img, file, this.THUMBNAIL_MAX_SIZE),
    ]);

    return {
      file: compressedResult.file,
      fileWidth: compressedResult.width,
      fileHeight: compressedResult.height,
      thumbnail: compressedThumbnail.file,
      thumbnailWidth: compressedThumbnail.width,
      thumbnailHeight: compressedThumbnail.height,
    };
  }

  /**
   * Loads a File into an HTMLImageElement.
   */
  async loadFileToHTMLImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Error loading image'));
      };

      img.src = url;
    });
  }

  private wasmInitPromise: Promise<void> | null = null;

  /** Initialize WASM module once. Safe for concurrent calls. */
  private ensureWasmInit(): Promise<void> {
    if (!this.wasmInitPromise) {
      this.wasmInitPromise = (async () => {
        const { init } = await import('@jsquash/webp/encode');
        await init({ locateFile: (path: string) => `/${path}` });
      })();
    }
    return this.wasmInitPromise;
  }

  /**
   * Core image compression. Always outputs webp.
   * Resizes to maxSize (longest edge) and encodes via jSquash.
   * Also reduces quality based on Compress_QUALITY_HIGH/LOW.
   */
  private async compressToWebp(
    img: HTMLImageElement,
    originalFile: File,
    maxSize: number
  ): Promise<{ file: File; width: number; height: number }> {
    // Ensure WASM is initialized (safe for concurrent calls)
    await this.ensureWasmInit();
    // Dynamic import to avoid loading it if not needed
    const { encode: encodeWebP } = await import('@jsquash/webp');

    let width = img.naturalWidth;
    let height = img.naturalHeight;

    // Resize: cap longest edge to maxSize, preserve aspect ratio
    if (width > height) {
      if (width > maxSize) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      }
    } else {
      if (height > maxSize) {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    const imageData = ctx.getImageData(0, 0, width, height);

    // HEIC/HEIF or large files get different quality (tunable for future)
    const ext = originalFile.name.split('.').pop()?.toLowerCase();
    const isHighQuality = ext === 'heic' || ext === 'heif' || originalFile.size > 2 * 1024 * 1024;
    const quality = isHighQuality ? this.COMPRESS_QUALITY_HIGH : this.COMPRESS_QUALITY_LOW;

    const webpData = await encodeWebP(imageData, { quality });

    const blob = new Blob([webpData], { type: 'image/webp' });
    const originalName = originalFile.name.split('.')[0];
    const compressedFile = new File([blob], `${originalName}.webp`, {
      type: 'image/webp',
      lastModified: originalFile.lastModified,
    });

    this.logger.log(
      `compressImage: ${originalFile.size} → ${compressedFile.size} bytes (${width}x${height})`
    );
    return { file: compressedFile, width, height };
  }

  // ─── Unified File Processor ─────────────────────────────────────────

  /**
   * Public API: processes any media file for upload.
   * - Images: compresses to webp + generates thumbnail
   * - Videos: extracts metadata + generates webp thumbnail at 1s
   * - Audio: returns file as-is with duration (future)
   */
  async processFileForUpload(file: File, onProgress?: (p: number) => void): Promise<ProcessedFile> {
    const mediaType = file.type.split('/')[0] as 'image' | 'video' | 'audio';

    switch (mediaType) {
      case 'image': {
        const result = await this.compressImageWithThumbnail(file);
        return {
          mediaType: 'image',
          file: result.file,
          fileWidth: result.fileWidth,
          fileHeight: result.fileHeight,
          thumbnail: result.thumbnail,
          thumbnailWidth: result.thumbnailWidth,
          thumbnailHeight: result.thumbnailHeight,
        };
      }
      case 'video': {
        const { video, cleanup } = await this.loadVideo(file);
        try {
          const thumbnail = await this.generateVideoThumbnail(video, file.name, 1);
          return {
            mediaType: 'video',
            file: file,
            thumbnail,
            fileWidth: video.videoWidth,
            fileHeight: video.videoHeight,
            duration: video.duration,
          };
        } finally {
          cleanup();
        }
      }
      case 'audio': {
        const result = await this.audioCompressor.compress(file, (progress) => {
          this.logger.log(`Audio compression progress: ${Math.round(progress * 100)}%`);
          if (onProgress) onProgress(progress);
        });
        this.logger.log(
          `Audio compressed: ${result.originalSize} → ${result.compressedSize} bytes, duration: ${result.duration}s`
        );
        return {
          mediaType: 'audio',
          file: result.file,
          fileWidth: 0,
          fileHeight: 0,
          duration: result.duration,
          exactDuration: result.exactDuration,
          originalSize: result.originalSize,
          originalFormat: result.originalFormat,
          bitrate: result.bitrate,
          channels: result.channels,
        };
      }
      default:
        throw new Error(`Unsupported media type: ${file.type}`);
    }
  }

  /**
   * Loads a File into an HTMLVideoElement with metadata ready.
   */
  private loadVideo(file: File): Promise<{ video: HTMLVideoElement; cleanup: () => void }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        resolve({
          video,
          cleanup: () => URL.revokeObjectURL(url),
        });
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Error loading video'));
      };

      video.src = url;
      video.load();
    });
  }

  /**
   * Captures a frame from a video at the given time and returns it as a webp File.
   */
  private generateVideoThumbnail(
    video: HTMLVideoElement,
    fileName: string,
    timeInSeconds: number
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const captureFrame = () => {
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const baseName = fileName.split('.')[0];
                const thumbnailFile = new File([blob], `${baseName}.${this.IMAGE_EXTENSION}`, {
                  type: `image/${this.IMAGE_EXTENSION}`,
                  lastModified: Date.now(),
                });
                resolve(thumbnailFile);
              } else {
                reject(new Error('Could not create video thumbnail'));
              }
            },
            `image/${this.IMAGE_EXTENSION}`,
            this.COMPRESS_QUALITY_LOW / 100
          );
        } catch (error) {
          reject(new Error('Error capturing video frame'));
        }
      };

      // If already at desired time, capture immediately
      if (video.currentTime >= timeInSeconds) {
        captureFrame();
        return;
      }

      video.currentTime = timeInSeconds;
      video.onseeked = () => captureFrame();
      video.onerror = () => reject(new Error('Error seeking video'));
    });
  }
}

/**
 * Result of processFileForUpload — everything needed for upload.
 */
export interface ProcessedFile {
  mediaType: 'image' | 'video' | 'audio';
  file: File;
  fileWidth: number;
  fileHeight: number;
  thumbnail?: File;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  duration?: number;
  /** Exact duration in seconds (raw float, used for metadata JSONB) */
  exactDuration?: number;
  originalSize?: number;
  originalFormat?: string;
  bitrate?: number;
  channels?: number;
}
